import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';

import {
  DEFAULT_MAX_DAYS_BACK,
  DateRangeFilterValue,
  EMPTY_DATE_RANGE,
  normalizeDateRange,
  presetRange,
  startOfDayDaysAgo,
} from './date-range-value';

@Component({
  selector: 'app-date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styleUrl: './date-range-filter.component.scss',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDatepickerModule, MatNativeDateModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DateRangeFilterComponent), multi: true },
  ],
})
export class DateRangeFilterComponent implements ControlValueAccessor, OnInit {
  @Input() maxDaysBack = DEFAULT_MAX_DAYS_BACK;

  readonly presets = [
    { label: '@preset1Day', days: 1 },
    { label: '@preset1Week', days: 7 },
    { label: '@preset2Weeks', days: 14 },
    { label: '@preset30Days', days: 30 },
  ];

  readonly range = new FormGroup({
    from: new FormControl<Date | null>(null),
    to: new FormControl<Date | null>(null),
  });

  minDate = new Date();
  maxDate = new Date();

  private value: DateRangeFilterValue = EMPTY_DATE_RANGE;
  private onChange: (value: DateRangeFilterValue) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {
    this.range.valueChanges.pipe(
      debounceTime(400),
      takeUntilDestroyed()
    ).subscribe(() => this.commitFormEdit());
  }

  ngOnInit(): void {
    this.refreshBounds();
  }

  writeValue(value: DateRangeFilterValue | null): void {
    this.setValue(normalizeDateRange(value, this.maxDaysBack));
  }

  registerOnChange(fn: (value: DateRangeFilterValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  applyPreset(days: number): void {
    this.setValue(presetRange(days));
    this.onChange(this.value);
    this.onTouched();
  }

  isPresetActive(days: number): boolean {
    return this.value.presetDays === days;
  }

  // Recomputed at open so a tab left up overnight can still pick "today", and a lit
  // preset chip re-anchors to the new day instead of describing yesterday's range.
  onPickerOpened(): void {
    this.refreshBounds();
    if (this.value.presetDays == null) return;
    const fresh = normalizeDateRange(this.value, this.maxDaysBack);
    if (fresh.from?.getTime() !== this.value.from?.getTime()) {
      this.setValue(fresh);
      this.onChange(fresh);
    }
  }

  // Real edits through the inputs or calendar drop the preset identity. No-op emissions
  // are skipped: the range inputs echo programmatic writes back through the sibling
  // input's ControlValueAccessor, so emitEvent:false alone doesn't silence them.
  private commitFormEdit(): void {
    const { from, to } = this.range.getRawValue();
    if (sameTime(from, this.value.from) && sameTime(to, this.value.to)) return;
    this.value = { from: from ?? null, to: to ?? null, presetDays: null };
    this.onChange(this.value);
    this.onTouched();
  }

  private setValue(value: DateRangeFilterValue): void {
    this.value = value;
    this.range.setValue({ from: value.from, to: value.to }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  private refreshBounds(): void {
    this.maxDate = new Date();
    this.minDate = startOfDayDaysAgo(this.maxDaysBack);
  }
}

function sameTime(a: Date | null | undefined, b: Date | null | undefined): boolean {
  return (a?.getTime() ?? null) === (b?.getTime() ?? null);
}
